import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import _debounce from 'lodash/debounce';
import _sortBy from 'lodash/sortBy';
import classNames from 'classnames/bind';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';

// Components
import Heading from '../Heading';
import QuantityControl from './partials/QuantityControl';
import Amount from '../Amount';
import VariationInfo from '../VariationInfo';
import AppLink from '../AppLink';
import ToggleArrow from '../ToggleArrow';
import Collapse from '../Collapse';

// Partials
import Sauces from './partials/Sauces';
import AddedSauce from './partials/AddedSauce';
import AddedIngredients from '../AddedIngredients';
import RemovedIngredients from '../RemovedIngredients';
import Gift from './partials/Gift';

// State
import { removeItem, updateItem } from '../../state/modules/cart/actions';

// Router
import { paths, getRoute } from '../../entry/routes';

// Utils
import { isBonus, isCombobox, isGift } from '../../utils/catalog';

// Icons
import RemoveIcon from '../../icons/close.svg';

// Styles
import styles from './CartItemCompact.styl';

const cx = classNames.bind(styles);

export default function CartItem(props) {
  const { className, good } = props;
  const {
    count,
    discount_price,
    hash,
    item,
    price,
    // status,
    type,
    old_price,
    updating,
    removing,
    deposit,
    deposit_type = '',
  } = good;
  const {
    // combobox_box_id,
    combo_box_items = [],
    added_ingredients = [],
    added_sauces = [],
    // category,
    // category_id,
    custom_name,
    // diameter,
    // dough,
    free_sauce_count = 0,
    // id,
    image,
    // is_stuffed_crust,
    // kind,
    name,
    removed_ingredients = [],
    sauces = [],
    // size,
    // stuffed_crust,
  } = item;

  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const [isCompositionOpen, setIsCompositionOpen] = useState(false);
  const unit = useSelector(state => state.city.userCity.currency?.unit);
  const isItGift = isGift(good);
  const isItBonus = isBonus(good);
  const isItCombobox = isCombobox(good);
  const isItBox = good?.type === 'pizza_box';
  const toggleComboComposition = e => {
    e.preventDefault();
    setIsCompositionOpen(prev => !prev);
  };

  const handleEditCombobox = e => {
    e.preventDefault();

    if (!item?.combo_box_id) {
      return;
    }

    history.push({
      pathname: getRoute('comboboxAlias', item.combo_box_id),
      state: {
        editCombobox: {
          hash,
          combo_box_id: item.combo_box_id,
          combo_box_items,
        },
      },
    });
  };

  const changeQuantity = quantity => {
    const saucesIds = added_sauces.map(sauce => sauce.id);

    dispatch(updateItem({ hash, type, item, sauces: saucesIds, count: quantity }));
  };

  const debouncedChangeQuantity = useCallback(
    _debounce(q => changeQuantity(q), 250),
    [],
  );

  const handleRemoveItem = () => {
    return dispatch(removeItem(hash));
  };

  const handleAddFreeSauce = newId => {
    const includeSauces = added_sauces.map(sauce => sauce.id);

    return dispatch(updateItem({ hash, type, item, count, sauces: [newId, ...includeSauces] }));
  };

  const handleRemoveFreeSauce = newId => {
    const removedIdx = added_sauces.findIndex(sauce => sauce.id === newId);
    const saucesIds = added_sauces.filter((_, idx) => idx !== removedIdx).map(sauce => sauce.id);

    return dispatch(updateItem({ hash, type, item, count, sauces: saucesIds }));
  };

  return (
    <div className={cx('CartItem', className, { CartItem_gift: isItGift && discount_price === 0 })}>
      <div className={cx('CartItem__body')}>
        <figure className={cx('CartItem__image', { CartItem__image_preview: !image })}>
          {image && <img src={image} alt={name} />}
        </figure>
        <div className={cx('CartItem__info')}>
          <Heading level={5} className={cx('CartItem__name')}>
            {custom_name || name}
          </Heading>
          <VariationInfo
            {...item}
            deposit_type={deposit_type}
            className={cx('CartItem__description')}
          />
          {deposit && (
            <div className={cx('CartItem__zlg')}>
              <div>{intl.formatMessage({ id: 'productcard.deposit' })}</div>
              <div>{deposit}</div>
              <div>{unit}</div>
            </div>
          )}
          {added_ingredients.length > 0 && (
            <AddedIngredients list={added_ingredients} item={item} />
          )}
          {removed_ingredients.length > 0 && <RemovedIngredients list={removed_ingredients} />}
          {added_sauces.length > 0 && (
            <div className={cx('CartItem__sauces')}>
              {added_sauces.map(sauce => (
                <AddedSauce key={sauce.hash} onRemove={handleRemoveFreeSauce} sauce={sauce} />
              ))}
            </div>
          )}
          {Boolean(free_sauce_count - added_sauces.length) && (
            <Sauces
              className={cx('CartItem__add-sauce')}
              sauces={sauces}
              addToCart={handleAddFreeSauce}
              disabled={updating}
            />
          )}
          {isItCombobox && (
            <>
              <div className={cx('CartItem__composition-actions')}>
                <AppLink
                  href="#"
                  className={cx('CartItem__composition-toggle')}
                  onClick={toggleComboComposition}
                >
                  {intl.formatMessage({
                    id: isCompositionOpen ? 'cart.composition.rollUp' : 'cart.composition.more',
                  })}
                  <ToggleArrow isOpen={isCompositionOpen} />
                </AppLink>
                {item?.combo_box_id && (
                  <AppLink href="#" className={cx('CartItem__edit')} onClick={handleEditCombobox}>
                    {intl.formatMessage({ id: 'link.edit' })}
                  </AppLink>
                )}
              </div>
              <Collapse isOpen={isCompositionOpen}>
                {_sortBy(combo_box_items, 'category_id').map(comboboxItem => (
                  <div key={comboboxItem.id} className={cx('CartItem__composition-item')}>
                    <Heading level={5} className={cx('CartItem__name')}>
                      {comboboxItem.custom_name || comboboxItem.name}
                    </Heading>
                    <VariationInfo {...comboboxItem} className={cx('CartItem__description')} />
                    {(comboboxItem.added_ingredients || []).length > 0 && (
                      <AddedIngredients list={comboboxItem.added_ingredients} />
                    )}
                    {(comboboxItem.removed_ingredients || []).length > 0 && (
                      <RemovedIngredients list={comboboxItem.removed_ingredients} />
                    )}
                  </div>
                ))}
              </Collapse>
            </>
          )}
        </div>
        {!isItGift && !isItBox && (
          <button
            className={cx('CartItem__remove')}
            onClick={handleRemoveItem}
            disabled={removing || updating}
            type="button"
          >
            <RemoveIcon />
          </button>
        )}
      </div>
      {isItGift && <Gift className={cx('CartItem__gift')} count={count} />}
      {(!isItGift || Boolean(discount_price)) && (
        <div className={cx('CartItem__footer')}>
          {!isItGift ? (
            <QuantityControl
              isItBox={isItBox}
              quantity={count}
              onChange={debouncedChangeQuantity}
              onRemove={handleRemoveItem}
              // max={isItUpsale ? 1 : undefined}
            />
          ) : (
            <div />
          )}
          {Boolean(discount_price) && (
            <Amount
              className={cx('CartItem__amount')}
              value={Math.max(old_price || price, price)}
              newValue={discount_price}
              bonus={isItBonus}
              size="s"
            />
          )}
        </div>
      )}
      {(updating || removing) && (
        <div className={cx('CartItem__loader')}>
          <div className={cx('CartItem__spinner')} />
        </div>
      )}
    </div>
  );
}

CartItem.defaultProps = {
  className: '',
};

CartItem.propTypes = {
  className: PropTypes.string,
  good: PropTypes.object.isRequired,
};
